import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Info } from 'lucide-react';
import type { ContextoInicialPeticao } from '../../tipos/tiposCriacaoPeticao';

interface PropsComponenteContextoInicial {
  contextoInicial?: ContextoInicialPeticao;
  onContextoConcluido: (contexto: ContextoInicialPeticao) => void;
  onErro?: (erro: string) => void;
}

const STORAGE_KEY = 'rascunho_contexto_inicial_peticao';

// Helpers
function formatCurrencyBRL(value?: number | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function parseCurrencyBRL(value: string): number | undefined {
  const cleaned = value.replace(/[R$\s.]/g, '').replace(',', '.');
  const num = Number(cleaned);
  if (Number.isNaN(num)) return undefined;
  return num;
}

export function ComponenteContextoInicial({
  contextoInicial,
  onContextoConcluido,
  onErro,
}: PropsComponenteContextoInicial): JSX.Element {
  // Estado do formulario
  const [descricaoCaso, setDescricaoCaso] = useState('');
  const [parteAutora, setParteAutora] = useState('');
  const [parteRe, setParteRe] = useState('');
  const [tipoAcao, setTipoAcao] = useState<string | undefined>(undefined);
  const [pedidosPretendidos, setPedidosPretendidos] = useState('');
  const [valorCausaInput, setValorCausaInput] = useState('');
  const [valorCausa, setValorCausa] = useState<number | undefined>(undefined);
  const [observacoes, setObservacoes] = useState('');

  // UI / control states
  const [submetendo, setSubmetendo] = useState(false);
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // Validations (local state error messages)
  const [errosCampo, setErrosCampo] = useState<Record<string, string>>({});

  // Carrega rascunho / contextoInicial
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      let draft: Partial<ContextoInicialPeticao> | null = null;

      if (raw) {
        draft = JSON.parse(raw) as Partial<ContextoInicialPeticao>;
      }

      const initial = contextoInicial ?? draft ?? {};

      if (initial.descricaoCaso) setDescricaoCaso(initial.descricaoCaso);
      if (initial.parteAutora) setParteAutora(initial.parteAutora);
      if (initial.parteRe) setParteRe(initial.parteRe);
      if (initial.tipoAcao) setTipoAcao(initial.tipoAcao);
      if (initial.pedidosPretendidos) setPedidosPretendidos(initial.pedidosPretendidos);
      if (initial.valorCausaEstimado) {
        setValorCausa(initial.valorCausaEstimado);
        setValorCausaInput(formatCurrencyBRL(initial.valorCausaEstimado));
      }
      if (initial.observacoes) setObservacoes(initial.observacoes);
    } catch (err) {
      console.error('Falha ao carregar rascunho do localStorage', err);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [contextoInicial]);

  // Debounced save to localStorage
  useEffect(() => {
    const payload: Partial<ContextoInicialPeticao> = {
      descricaoCaso,
      parteAutora,
      parteRe,
      tipoAcao,
      pedidosPretendidos,
      valorCausaEstimado: valorCausa,
      observacoes,
    };

    const handle = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (err) {
        console.error('Falha ao salvar rascunho no localStorage', err);
      }
    }, 1000);

    return () => clearTimeout(handle);
  }, [descricaoCaso, parteAutora, parteRe, tipoAcao, pedidosPretendidos, valorCausa, observacoes]);

  // Validators
  function validarCampos(): boolean {
    const erros: Record<string, string> = {};

    if (!descricaoCaso || descricaoCaso.trim().length < 100) {
      erros.descricaoCaso = 'Descreva seu caso (mínimo 100 caracteres).';
    } else if (descricaoCaso.length > 5000) {
      erros.descricaoCaso = 'Descrição muito longa (máx. 5000 caracteres).';
    }

    if (!parteAutora || parteAutora.trim().length === 0) {
      erros.parteAutora = 'Informe o nome completo do(a) autor(a).';
    }

    if (pedidosPretendidos && pedidosPretendidos.length > 2000) {
      erros.pedidosPretendidos = 'Texto muito longo (máx. 2000 caracteres).';
    }

    if (observacoes && observacoes.length > 1000) {
      erros.observacoes = 'Texto muito longo (máx. 1000 caracteres).';
    }

    setErrosCampo(erros);
    return Object.keys(erros).length === 0;
  }

  // Real-time validation
  useEffect(() => {
    validarCampos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descricaoCaso, parteAutora, pedidosPretendidos, observacoes]);

  // Memo for button disabled
  const isFormValid = useMemo(() => {
    return (
      descricaoCaso && descricaoCaso.trim().length >= 100 &&
      parteAutora && parteAutora.trim().length > 0 &&
      descricaoCaso.length <= 5000 &&
      (pedidosPretendidos ? pedidosPretendidos.length <= 2000 : true) &&
      (observacoes ? observacoes.length <= 1000 : true)
    );
  }, [descricaoCaso, parteAutora, pedidosPretendidos, observacoes]);

  // Handlers
  function handleValorCausaChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    // Accept digits, dot, comma, R, $, whitespace
    const parsed = parseCurrencyBRL(value);
    setValorCausaInput(value);
    setValorCausa(parsed);
  }

  function handleContinuar() {
    setSubmetendo(true);
    setErroGeral(null);

    const valido = validarCampos();
    if (!valido) {
      setSubmetendo(false);
      setErroGeral('Existem erros no formulário. Corrija antes de continuar.');
      if (onErro) onErro('Formulário inválido');

      // Scroll to first error field
      const firstKey = Object.keys(errosCampo)[0];
      if (firstKey) {
        const el = document.querySelector(`[name="${firstKey}"]`);
        if (el && typeof (el as HTMLElement).focus === 'function') {
          (el as HTMLElement).focus();
        }
      }

      return;
    }

    try {
      const payload: ContextoInicialPeticao = {
        descricaoCaso: descricaoCaso.trim(),
        tipoAcao: tipoAcao,
        parteAutora: parteAutora.trim(),
        parteRe: parteRe?.trim() || undefined,
        pedidosPretendidos: pedidosPretendidos?.trim() || undefined,
        valorCausaEstimado: valorCausa,
        observacoes: observacoes?.trim() || undefined,
      };

      // Clear draft
      localStorage.removeItem(STORAGE_KEY);

      onContextoConcluido(payload);
      setSubmetendo(false);
    } catch (err) {
      console.error(err);
      const mensagem = 'Falha ao avançar com o contexto. Tente novamente.';
      setErroGeral(mensagem);
      if (onErro) onErro(mensagem);
      setSubmetendo(false);
    }
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Contexto inicial</h2>
            <p className="text-sm text-gray-600">Forneça detalhes do caso para que a IA possa iniciar a análise.</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Info className="size-5" />
            <span>Preencha com o máximo de informações relevantes</span>
          </div>
        </div>

        {/* DESCRIÇÃO DO CASO */}
        <div className="grid grid-cols-1 gap-2">
          <label htmlFor="descricaoCaso" className="font-medium text-gray-700">Descreva seu caso</label>
          <textarea
            id="descricaoCaso"
            name="descricaoCaso"
            aria-label="Descreva seu caso"
            rows={6}
            placeholder="Conte-nos detalhadamente o que aconteceu e por que você precisa entrar com esta ação..."
            value={descricaoCaso}
            onChange={(e) => setDescricaoCaso(e.target.value)}
            className={`rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${errosCampo.descricaoCaso ? 'border-red-400' : 'border-gray-200'}`}
            aria-invalid={!!errosCampo.descricaoCaso}
            aria-describedby={errosCampo.descricaoCaso ? 'descricaoCaso-erro' : undefined}
          />

          <div className="flex items-center justify-between">
            <div className="text-sm text-red-600" role="alert" id="descricaoCaso-erro">{errosCampo.descricaoCaso}</div>
            <div className="text-sm text-gray-500">{descricaoCaso.length}/5000</div>
          </div>
        </div>

        {/* PARTE AUTORA */}
        <div className="grid grid-cols-1 gap-2">
          <label htmlFor="parteAutora" className="font-medium text-gray-700">Nome completo do(a) autor(a)</label>
          <input
            id="parteAutora"
            name="parteAutora"
            type="text"
            value={parteAutora}
            onChange={(e) => setParteAutora(e.target.value)}
            className={`rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${errosCampo.parteAutora ? 'border-red-400' : 'border-gray-200'}`}
            placeholder="Ex: João Silva"
            aria-invalid={!!errosCampo.parteAutora}
            aria-describedby={errosCampo.parteAutora ? 'parteAutora-erro' : undefined}
          />
          <div className="text-sm text-red-600" role="alert" id="parteAutora-erro">{errosCampo.parteAutora}</div>
        </div>

        {/* PARTE RÉ */}
        <div className="grid grid-cols-1 gap-2">
          <label htmlFor="parteRe" className="font-medium text-gray-700">Nome do(a) réu/reclamado(a)</label>
          <input
            id="parteRe"
            name="parteRe"
            type="text"
            value={parteRe}
            onChange={(e) => setParteRe(e.target.value)}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Ex: Empresa XYZ Ltda"
          />
          <div className="text-xs text-gray-500">Se souber quem será processado, informe aqui</div>
        </div>

        {/* TIPO DE AÇÃO */}
        <div className="grid grid-cols-1 gap-2">
          <label htmlFor="tipoAcao" className="font-medium text-gray-700">Tipo de ação</label>
          <select
            id="tipoAcao"
            name="tipoAcao"
            value={tipoAcao || ''}
            onChange={(e) => setTipoAcao(e.target.value || undefined)}
            className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Selecione</option>
            <option value="trabalhista">Trabalhista</option>
            <option value="civel">Cível</option>
            <option value="previdenciario">Previdenciário</option>
            <option value="tributario">Tributário</option>
            <option value="outro">Outro</option>
          </select>
          <div className="text-xs text-gray-500">A IA pode inferir automaticamente se não souber</div>
        </div>



        {/* PEDIDOS PRETENDIDOS */}
        <div className="grid grid-cols-1 gap-2">
          <label htmlFor="pedidosPretendidos" className="font-medium text-gray-700">O que você deseja obter com esta ação?</label>
          <textarea
            id="pedidosPretendidos"
            name="pedidosPretendidos"
            rows={3}
            placeholder="Ex: Indenização por danos morais, rescisão contratual, pagamento de verbas rescisórias..."
            value={pedidosPretendidos}
            onChange={(e) => setPedidosPretendidos(e.target.value)}
            className={`rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${errosCampo.pedidosPretendidos ? 'border-red-400' : 'border-gray-200'}`}
            aria-invalid={!!errosCampo.pedidosPretendidos}
            aria-describedby={errosCampo.pedidosPretendidos ? 'pedidosPretendidos-erro' : undefined}
          />
          <div className="text-sm text-red-600" role="alert" id="pedidosPretendidos-erro">{errosCampo.pedidosPretendidos}</div>
        </div>



        {/* VALOR DA CAUSA ESTIMADO */}
        <div className="max-w-sm">
          <div className="grid grid-cols-1 gap-2">
            <label htmlFor="valorCausa" className="font-medium text-gray-700">Valor estimado da causa (R$)</label>
            <input
              id="valorCausa"
              name="valorCausa"
              type="text"
              inputMode="numeric"
              value={valorCausaInput}
              onChange={handleValorCausaChange}
              onBlur={() => setValorCausaInput(formatCurrencyBRL(valorCausa))}
              placeholder="Ex: 50.000,00"
              className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <div className="text-xs text-gray-500">Estimativa do valor que você pretende obter</div>
          </div>
        </div>

        {/* OBSERVAÇÕES ADICIONAIS */}
        <div className="grid grid-cols-1 gap-2">
          <label htmlFor="observacoes" className="font-medium text-gray-700">Observações adicionais</label>
          <textarea
            id="observacoes"
            name="observacoes"
            rows={3}
            placeholder="Qualquer outra informação que considere relevante..."
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            className={`rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${errosCampo.observacoes ? 'border-red-400' : 'border-gray-200'}`}
            aria-invalid={!!errosCampo.observacoes}
            aria-describedby={errosCampo.observacoes ? 'observacoes-erro' : undefined}
          />
          <div className="flex items-center justify-between">
            <div className="text-sm text-red-600" role="alert" id="observacoes-erro">{errosCampo.observacoes}</div>
            <div className="text-sm text-gray-500">{observacoes.length}/1000</div>
          </div>
        </div>

        {/* ERRO GERAL */}
        {erroGeral && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            {erroGeral}
          </div>
        )}

        {/* AÇÕES */}
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={handleContinuar}
            disabled={!isFormValid || submetendo}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span>Continuar</span>
            <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
